// Adaptive Module 7: Beverage Reasoning (Deductive Reasoning)
// Replaces Module7_1 through Module7_3 with adaptive difficulty

import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from 'antd';
import { useRecoilState } from 'recoil';
import { sessionTimingAtom } from '../../../../state/assessment';
import AssessmentWrapper from '../../AssessmentWrapper';
import Close from '../../../../resources/imgs/modal/close.svg';
import { FaChevronRight } from 'react-icons/fa';
import Progress from '../../progress';
import HelpBtn from '../../../../resources/imgs/assessment/helpBtn.svg';

// Import beverage images dynamically based on filename pattern
const getBeverageImage = (size, temperature, type) => {
  try {
    return require(`../../../../resources/imgs/assessment/beverages/${size}_${temperature}_${type}.png`);
  } catch (error) {
    console.error(`Image not found: ${size}_${temperature}_${type}.png`);
    return null;
  }
};

// Helper function to format temperature for display
const formatTemperature = (temp) => {
  if (temp === 'room') return 'room temperature';
  return temp;
};

// Module 7 instances from the markdown file - organized by difficulty
const MODULE_7_INSTANCES = {
  1: {
    id: "d1",
    difficulty: 1,
    title: "One Attribute: Temperature",
    pattern: "one-attribute",
    target_attribute: "temperature",
    shared_value: "hot",
    options: [
      { index: 1, type: "coffee", temperature: "hot", size: "small", asset: "small_hot_coffee.png" },
      { index: 2, type: "tea", temperature: "hot", size: "medium", asset: "medium_hot_tea.png" },
      { index: 3, type: "cider", temperature: "hot", size: "large", asset: "large_hot_cider.png" },
      { index: 4, type: "tea", temperature: "room", size: "medium", asset: "medium_room_tea.png" }
    ],
    solution_index: 4,
    explanation: "Three drinks are served hot (options 1–3); option 4 is room temperature."
  },
  2: {
    id: "d2",
    difficulty: 2,
    title: "One Attribute: Size",
    pattern: "one-attribute",
    target_attribute: "size",
    shared_value: "medium",
    options: [
      { index: 1, type: "coffee", temperature: "cold", size: "medium", asset: "medium_cold_coffee.png" },
      { index: 2, type: "tea", temperature: "room", size: "medium", asset: "medium_room_tea.png" },
      { index: 3, type: "cider", temperature: "hot", size: "medium", asset: "medium_hot_cider.png" },
      { index: 4, type: "tea", temperature: "cold", size: "large", asset: "large_cold_tea.png" }
    ],
    solution_index: 4,
    explanation: "All but option 4 are medium; option 4 is large."
  },
  3: {
    id: "d3",
    difficulty: 3,
    title: "One Attribute: Type",
    pattern: "one-attribute",
    target_attribute: "type",
    shared_value: "coffee",
    options: [
      { index: 1, type: "coffee", temperature: "room", size: "small", asset: "small_room_coffee.png" },
      { index: 2, type: "coffee", temperature: "hot", size: "large", asset: "large_hot_coffee.png" },
      { index: 3, type: "coffee", temperature: "cold", size: "medium", asset: "medium_cold_coffee.png" },
      { index: 4, type: "tea", temperature: "cold", size: "medium", asset: "medium_cold_tea.png" }
    ],
    solution_index: 4,
    explanation: "Three drinks are coffee (options 1–3); option 4 is tea."
  },
  4: {
    id: "d4",
    difficulty: 4,
    title: "One Attribute: Temperature",
    pattern: "one-attribute",
    target_attribute: "temperature",
    shared_value: "room",
    options: [
      { index: 1, type: "tea", temperature: "room", size: "small", asset: "small_room_tea.png" },
      { index: 2, type: "cider", temperature: "room", size: "medium", asset: "medium_room_cider.png" },
      { index: 3, type: "coffee", temperature: "room", size: "large", asset: "large_room_coffee.png" },
      { index: 4, type: "cider", temperature: "cold", size: "medium", asset: "medium_cold_cider.png" }
    ],
    solution_index: 4,
    explanation: "Three drinks are room temperature (options 1–3); option 4 is cold."
  },
  5: {
    id: "d5",
    difficulty: 5,
    title: "One Attribute: Size",
    pattern: "one-attribute",
    target_attribute: "size",
    shared_value: "small",
    options: [
      { index: 1, type: "coffee", temperature: "hot", size: "small", asset: "small_hot_coffee.png" },
      { index: 2, type: "tea", temperature: "cold", size: "small", asset: "small_cold_tea.png" },
      { index: 3, type: "cider", temperature: "room", size: "small", asset: "small_room_cider.png" },
      { index: 4, type: "tea", temperature: "hot", size: "medium", asset: "medium_hot_tea.png" }
    ],
    solution_index: 4,
    explanation: "The table orders are all small; option 4 is medium."
  },
  6: {
    id: "d6",
    difficulty: 6,
    title: "Two Attributes: Type–Temperature Pairing",
    pattern: "two-attributes",
    constraints: {
      by_type: {
        coffee: { temperature: "hot" },
        tea: { temperature: "cold" },
        cider: { temperature: "room" }
      }
    },
    options: [
      { index: 1, type: "coffee", temperature: "hot", size: "small", asset: "small_hot_coffee.png" },
      { index: 2, type: "tea", temperature: "cold", size: "medium", asset: "medium_cold_tea.png" },
      { index: 3, type: "cider", temperature: "room", size: "large", asset: "large_room_cider.png" },
      { index: 4, type: "tea", temperature: "hot", size: "medium", asset: "medium_hot_tea.png" }
    ],
    solution_index: 4,
    explanation: "Options 1–3 follow the rule (coffee hot, tea cold, cider room temperature). Option 4 shows tea hot."
  },
  7: {
    id: "d7",
    difficulty: 7,
    title: "Two Attributes: Temperature–Size Pairing",
    pattern: "two-attributes",
    constraints: {
      by_temperature: {
        hot: { size: "small" },
        cold: { size: "medium" },
        room: { size: "large" }
      }
    },
    options: [
      { index: 1, type: "coffee", temperature: "hot", size: "small", asset: "small_hot_coffee.png" },
      { index: 2, type: "tea", temperature: "cold", size: "medium", asset: "medium_cold_tea.png" },
      { index: 3, type: "cider", temperature: "room", size: "large", asset: "large_room_cider.png" },
      { index: 4, type: "cider", temperature: "hot", size: "large", asset: "large_hot_cider.png" }
    ],
    solution_index: 4,
    explanation: "Options 1–3 obey the temperature–size pairing; option 4 is hot but large (hot should be small)."
  },
  8: {
    id: "d8",
    difficulty: 8,
    title: "Two Attributes: Type–Size Pairing",
    pattern: "two-attributes",
    constraints: {
      by_type: {
        coffee: { size: "small" },
        tea: { size: "medium" },
        cider: { size: "large" }
      }
    },
    options: [
      { index: 1, type: "coffee", temperature: "hot", size: "small", asset: "small_hot_coffee.png" },
      { index: 2, type: "tea", temperature: "room", size: "medium", asset: "medium_room_tea.png" },
      { index: 3, type: "cider", temperature: "cold", size: "large", asset: "large_cold_cider.png" },
      { index: 4, type: "cider", temperature: "hot", size: "small", asset: "small_hot_cider.png" }
    ],
    solution_index: 4,
    explanation: "Options 1–3 follow the type–size rule; option 4 pairs cider with small (should be large)."
  },
  9: {
    id: "d9",
    difficulty: 9,
    title: "Three Attributes: Specific Combination #1",
    pattern: "three-attributes",
    constraints: {
      by_type: {
        coffee: { temperature: "hot", size: "small" },
        tea: { temperature: "room", size: "medium" },
        cider: { temperature: "cold", size: "large" }
      }
    },
    options: [
      { index: 1, type: "coffee", temperature: "hot", size: "small", asset: "small_hot_coffee.png" },
      { index: 2, type: "tea", temperature: "room", size: "medium", asset: "medium_room_tea.png" },
      { index: 3, type: "cider", temperature: "cold", size: "large", asset: "large_cold_cider.png" },
      { index: 4, type: "coffee", temperature: "hot", size: "medium", asset: "medium_hot_coffee.png" }
    ],
    solution_index: 4,
    explanation: "Options 1–3 follow the rule; option 4 is coffee but medium instead of small."
  },
  10: {
    id: "d10",
    difficulty: 10,
    title: "Three Attributes: Specific Combination #2",
    pattern: "three-attributes",
    constraints: {
      by_type: {
        coffee: { temperature: "room", size: "medium" },
        tea: { temperature: "hot", size: "small" },
        cider: { temperature: "cold", size: "large" }
      }
    },
    options: [
      { index: 1, type: "coffee", temperature: "room", size: "medium", asset: "medium_room_coffee.png" },
      { index: 2, type: "tea", temperature: "hot", size: "small", asset: "small_hot_tea.png" },
      { index: 3, type: "cider", temperature: "cold", size: "large", asset: "large_cold_cider.png" },
      { index: 4, type: "tea", temperature: "cold", size: "small", asset: "small_cold_tea.png" }
    ],
    solution_index: 4,
    explanation: "Options 1–3 conform to the rule; option 4 is tea but cold instead of hot."
  }
};

// Helper function to shuffle array
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const BeverageReasoningContent = ({ instance, onSubmit, instanceNumber, moduleId }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [open, setOpen] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [sessionTiming, setSessionTiming] = useRecoilState(sessionTimingAtom);
  const [instanceStartTime, setInstanceStartTime] = useState(null);

  // Reset state and shuffle options when instance changes
  useEffect(() => {
    setSelectedOption(null);
    setShowResult(false);
    setSubmitted(false);

    // Initialize session start time if not already set
    const now = Date.now();
    if (!sessionTiming.sessionStartTime) {
      setSessionTiming(prev => ({
        ...prev,
        sessionStartTime: now
      }));
    }

    // Record instance start time
    setInstanceStartTime(now);
    console.log(`⏱️ Module 7 Instance ${instanceNumber} started at ${now - (sessionTiming.sessionStartTime || now)}ms from session start`);

    // Shuffle the options to randomize correct answer position
    if (instance && instance.options) {
      const shuffled = shuffleArray(instance.options);
      setShuffledOptions(shuffled);

      // Log where the correct answer ended up
      const correctPosition = shuffled.findIndex(opt => opt.index === instance.solution_index);
      console.log(`🎲 Module 7 options shuffled. Correct answer (index ${instance.solution_index}) now at position: ${correctPosition + 1}/4`);
    }
  }, [instance, sessionTiming.sessionStartTime, instanceNumber]);

  const showModal = () => setOpen(true);
  const hideModal = () => setOpen(false);

  const handleOptionClick = useCallback((optionIndex) => {
    if (showResult || submitted) return;
    setSelectedOption(optionIndex);
  }, [showResult, submitted]);

  const handleNext = useCallback(() => {
    if (!selectedOption || submitted) return;

    const isCorrect = selectedOption === instance.solution_index;
    setShowResult(true);
    setSubmitted(true);

    // Record trial timing data
    const now = Date.now();
    const sessionStart = sessionTiming.sessionStartTime || now;
    const trialStart = instanceStartTime || now;

    const trialData = {
      module_id: '7',
      instance_id: instance?.id?.toString() || instanceNumber?.toString(),
      timestamp_start_ms: trialStart - sessionStart,
      timestamp_response_ms: now - sessionStart,
      response_latency_ms: now - trialStart,
      is_correct: isCorrect,
      error_type: isCorrect ? 'none' : 'accuracy',
      difficulty_level: instance.difficulty,
      trial_number: instanceNumber
    };

    console.log('⏱️ Module 7 trial data:', trialData);

    // Add to session timing atom
    setSessionTiming(prev => ({
      ...prev,
      trialData: [...prev.trialData, trialData]
    }));

    // Submit result after brief delay to show feedback
    setTimeout(() => {
      onSubmit({
        selectedAnswer: selectedOption,
        correctAnswer: instance.solution_index,
        isCorrect,
        responseTime: now - trialStart,
        trialData
      });
    }, 1500);
  }, [selectedOption, instance, onSubmit, submitted, sessionTiming, instanceStartTime, instanceNumber, setSessionTiming]);

  if (!instance || shuffledOptions.length === 0) return <div>Loading beverage puzzle...</div>;

  return (
    <div className="module1-main">
      <div className="module1-wrap">
        <Progress number="07." percent={7 / 8} />
        <div className="module1-content-wrap">
          <div className="module1-logo">Module 7</div>
          <div className="module1-body">
            <div className="module1-title">Which beverage is the odd one out?</div>
            <div className="module1-content1">
              Instance {instanceNumber} - Difficulty {instance.difficulty}
            </div>
          </div>
        </div>
      </div>

      <div className="module1-categories"></div>
      <div className="module1-dishes module1-dishes2 module7-dishes">
        {shuffledOptions.map((option) => {
          const image = getBeverageImage(option.size, option.temperature, option.type);
          const isSelected = selectedOption === option.index;
          const isCorrect = option.index === instance.solution_index;
          
          let className = "module1-dish";
          let style = { cursor: showResult ? 'default' : 'pointer' };
          
          if (isSelected && !showResult) {
            style.backgroundColor = "#F277C6";
            style.border = "2px solid #F277C6";
          }
          if (showResult) {
            if (isSelected && isCorrect) {
              className += " correct-answer";
              style.backgroundColor = "#28a745";
            } else if (isSelected && !isCorrect) {
              className += " wrong-answer";
              style.backgroundColor = "#dc3545";
            } else if (isCorrect) {
              className += " correct-answer";
              style.backgroundColor = "#28a745";
            }
          }

          return (
            <div
              key={option.index}
              className={className}
              onClick={() => handleOptionClick(option.index)}
              style={style}
            >
              {image && <img src={image} alt={`${option.size} ${formatTemperature(option.temperature)} ${option.type}`} />}
              <div className="beverage-info">
                {option.size} {option.type} ({formatTemperature(option.temperature)})
              </div>
            </div>
          );
        })}
      </div>

      <div className='next-btn-group'>
        <div className='help-btn-group'>
          <div className='help-btn-effect1'></div>
          <div className='help-btn-effect2'></div>
          <img src={HelpBtn} onClick={showModal} alt="Help" />
          <div className='help-content'>Do you need help?</div>
        </div>
        {selectedOption && !showResult && (
          <div className='assessment-next-btn' onClick={handleNext}>
            Next
            <FaChevronRight />
          </div>
        )}
      </div>

      <Modal
        open={open}
        onOk={showModal}
        onCancel={hideModal}
        closeIcon={<img src={Close} alt="Close" />}
        footer={null}
      >
        <div className="direction-modal-container">
          <div className='direction-modal-title'>Directions</div>
          <div className='direction-modal-content'>
            Find the beverage that doesn't follow the same pattern as the others. Look for differences in type (coffee, tea, cider), size (small, medium, large), or temperature (hot, room temperature, cold).
          </div>
        </div>
      </Modal>
    </div>
  );
};

const AdaptiveModule7 = ({ onModuleComplete, moduleType }) => {
  console.log('🎯 AdaptiveModule7 rendering with:', { onModuleComplete: !!onModuleComplete, moduleType });
  
  // Use AssessmentWrapper for proper adaptive functionality
  const renderContent = ({ instance, onSubmit, instanceNumber, moduleId }) => {
    const handleComplete = (data) => {
      console.log(`🎯 Module 7 adaptive handleComplete received:`, data);
      if (onSubmit) {
        onSubmit(data.isCorrect, {
          ...data,
          responseTime: Date.now() - (instance.startTime || Date.now())
        });
      }
    };

    return (
      <BeverageReasoningContent 
        instance={instance}
        onSubmit={handleComplete}
        instanceNumber={instanceNumber}
        moduleId={moduleId}
      />
    );
  };

  return (
    <AssessmentWrapper
      moduleType="7"
      onModuleComplete={onModuleComplete}
      isAdaptive={true}
      renderAdaptiveContent={renderContent}
    />
  );
};

export default AdaptiveModule7;